<template>
  <Transition name="slide-up">
    <div v-if="visible" class="push-prompt" role="dialog" :aria-label="t('push.aria_label')">
      <div class="push-prompt__icon">&#128276;</div>
      <div class="push-prompt__content">
        <p class="push-prompt__title">{{ t('push.title') }}</p>
        <p class="push-prompt__body">{{ t('push.body') }}</p>
      </div>
      <div class="push-prompt__actions">
        <button class="btn btn-primary btn-sm" :disabled="subscribing" @click="handleEnable">
          {{ subscribing ? t('push.enabling') : t('push.enable') }}
        </button>
        <button class="btn btn-ghost btn-sm" @click="handleDismiss">{{ t('push.not_now') }}</button>
      </div>
      <button class="push-prompt__close" :aria-label="t('common.close')" @click="handleDismiss">&#10005;</button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import apiClient from '@/api/client'

const { t } = useI18n()

// ─── Constants ────────────────────────────────────────────────────────────────
const STORAGE_KEY_DISMISSED = 'push_prompt_dismissed_at'
const STORAGE_KEY_VISIT_COUNT = 'push_prompt_visit_count'
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000 // 7 days
const SHOW_DELAY_MS = 30_000 // 30 seconds

// ─── State ────────────────────────────────────────────────────────────────────
const visible = ref(false)
const subscribing = ref(false)
let showTimer: ReturnType<typeof setTimeout> | null = null

const userStore = useUserStore()

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a Base64Url-encoded VAPID public key into a Uint8Array<ArrayBuffer>. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const buffer = new ArrayBuffer(rawData.length)
  const outputArray = new Uint8Array(buffer)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function shouldShow(): boolean {
  // Requires browser support
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false
  }

  // Only show when logged in
  if (!userStore.isAuthenticated) {
    return false
  }

  // Already granted or denied — no point prompting
  if (Notification.permission !== 'default') {
    return false
  }

  // Check 7-day dismiss cooldown
  const dismissedAt = localStorage.getItem(STORAGE_KEY_DISMISSED)
  if (dismissedAt) {
    const elapsed = Date.now() - parseInt(dismissedAt, 10)
    if (elapsed < DISMISS_COOLDOWN_MS) {
      return false
    }
  }

  // Require at least a second visit
  const visitCount = parseInt(localStorage.getItem(STORAGE_KEY_VISIT_COUNT) ?? '0', 10)
  if (visitCount < 2) {
    return false
  }

  return true
}

function recordVisit(): void {
  const count = parseInt(localStorage.getItem(STORAGE_KEY_VISIT_COUNT) ?? '0', 10)
  localStorage.setItem(STORAGE_KEY_VISIT_COUNT, String(count + 1))
}

// ─── Actions ──────────────────────────────────────────────────────────────────

function handleDismiss(): void {
  visible.value = false
  localStorage.setItem(STORAGE_KEY_DISMISSED, String(Date.now()))
}

async function handleEnable(): Promise<void> {
  subscribing.value = true

  try {
    // Request browser permission — must be triggered by user interaction
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      // Player denied — store a dismiss so we don't re-prompt immediately
      handleDismiss()
      return
    }

    // Fetch VAPID public key from the server
    const { data } = await apiClient.get<{ public_key: string }>('/api/push/vapid-key')
    const vapidPublicKey = data.public_key

    if (!vapidPublicKey) {
      throw new Error('VAPID public key not available')
    }

    // Subscribe via the browser PushManager
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })

    const json = subscription.toJSON()
    const keys = json.keys as { p256dh: string; auth: string } | undefined

    await apiClient.post('/api/push/subscribe', {
      endpoint: subscription.endpoint,
      p256dh: keys?.p256dh ?? '',
      auth: keys?.auth ?? '',
      user_agent: navigator.userAgent,
    })

    visible.value = false
  } catch {
    // Silently fail — don't disrupt the user experience
    visible.value = false
  } finally {
    subscribing.value = false
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  recordVisit()

  if (shouldShow()) {
    showTimer = setTimeout(() => {
      // Re-check auth state after the delay in case the user logged out
      if (userStore.isAuthenticated && Notification.permission === 'default') {
        visible.value = true
      }
    }, SHOW_DELAY_MS)
  }
})

onUnmounted(() => {
  if (showTimer !== null) {
    clearTimeout(showTimer)
  }
})
</script>

<style scoped>
.push-prompt {
  position: fixed;
  bottom: 80px; /* above mobile bottom nav */
  left: 50%;
  transform: translateX(-50%);
  z-index: 900;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bg-secondary, #1a2632);
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.1));
  border-radius: var(--radius-lg, 12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-width: 480px;
  width: calc(100vw - 32px);
}

@media (min-width: 768px) {
  .push-prompt {
    bottom: 24px;
  }
}

.push-prompt__icon {
  font-size: 24px;
  flex-shrink: 0;
}

.push-prompt__content {
  flex: 1;
  min-width: 0;
}

.push-prompt__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 2px;
}

.push-prompt__body {
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.push-prompt__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.push-prompt__close {
  position: absolute;
  top: 6px;
  right: 8px;
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  font-size: 10px;
  padding: 2px 4px;
  line-height: 1;
}

.push-prompt__close:hover {
  color: var(--text-primary, #fff);
}

/* Buttons */
.btn {
  border: none;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  font-weight: 600;
  transition: opacity var(--transition-fast, 150ms ease);
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.8rem;
}

.btn-primary {
  background: var(--color-primary, #1475e1);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
}

.btn-ghost:hover {
  color: var(--text-primary, #fff);
}

/* Transition */
.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
</style>
